import { Injectable } from '@angular/core';
import { LocalStorageKey, OnboardingTourName } from '../enums';
import { TagRequest } from '../models/tag';
import { LocalRecord } from '../models/local-record';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
  get(key: LocalStorageKey) {
    return localStorage.getItem(key);
  }

  set(key: LocalStorageKey, value: any) {
    const setValue =
      !!value && typeof value === 'object' ? JSON.stringify(value) : value;
    return localStorage.setItem(key, setValue ?? '');
  }

  delete(key: LocalStorageKey) {
    return localStorage.removeItem(key);
  }

  clearExpiredRecords(key: LocalStorageKey) {
    let existingValue = this.get(key);
    if (!existingValue) {
      return [];
    }
    const parsed = JSON.parse(existingValue!);
    if (Array.isArray(parsed)) {
      if (!parsed.length) {
        return parsed;
      }
      this.set(
        key,
        (parsed as LocalRecord[]).filter(
          (record) =>
            !record.expireDate ||
            new Date(record.expireDate).getTime() < Date.now()
        )
      );
      return JSON.parse(this.get(key)!);
    } else {
      return [];
    }
  }

  get mutedOnboardingTours(): Set<OnboardingTourName> {
    let existingValue = this.get(LocalStorageKey.MutedOnboardingTours);
    if (!existingValue) {
      this.set(LocalStorageKey.MutedOnboardingTours, []);
    }

    return new Set<OnboardingTourName>(
      JSON.parse(this.get(LocalStorageKey.MutedOnboardingTours)!)
    );
  }

  muteOnboardingTour(guideName: OnboardingTourName): void {
    const onboardingTours = this.mutedOnboardingTours;
    onboardingTours.add(guideName);
    this.set(LocalStorageKey.MutedOnboardingTours, Array.from(onboardingTours));
  }

  isOnboardingTourMuted(guideName?: OnboardingTourName): boolean {
    if (!guideName) {
      return false;
    }
    return this.mutedOnboardingTours.has(guideName);
  }

  get pendingTagScans(): TagRequest[] {
    let existingValue = this.get(LocalStorageKey.PendingTagScans);
    if (!existingValue) {
      this.set(LocalStorageKey.PendingTagScans, []);
    }

    return JSON.parse(this.get(LocalStorageKey.PendingTagScans)!);
  }

  savePendingTagScan(newPendingTagScan: TagRequest) {
    const pendingTagScans = this.pendingTagScans;
    if (
      !pendingTagScans.some(
        (savedPendingTagScan) =>
          savedPendingTagScan.tagId === newPendingTagScan.tagId &&
          savedPendingTagScan.tac === newPendingTagScan.tac
      )
    ) {
      pendingTagScans.push(newPendingTagScan);
      pendingTagScans.length
        ? this.set(LocalStorageKey.PendingTagScans, pendingTagScans)
        : this.delete(LocalStorageKey.PendingTagScans);
    }
  }

  deletePendingTagScan(pendingTagScanToBeDeleted: TagRequest) {
    const pendingTagScans = this.pendingTagScans;
    const savedPendingTagScanIndex = pendingTagScans.findIndex(
      (savedPendingTagScan) =>
        savedPendingTagScan.tagId === pendingTagScanToBeDeleted.tagId &&
        savedPendingTagScan.tac === pendingTagScanToBeDeleted.tac
    );
    if (savedPendingTagScanIndex >= 0) {
      const newArray = Array.from(pendingTagScans);
      newArray.splice(savedPendingTagScanIndex, 1);
      newArray.length
        ? this.set(LocalStorageKey.PendingTagScans, newArray)
        : this.delete(LocalStorageKey.PendingTagScans);
    }
  }
}
